import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  signal
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {MatDividerModule} from '@angular/material/divider';
import {TranslateModule} from '@ngx-translate/core';
import {map, of} from 'rxjs';
import {TagComponent} from '@q9elements/ui-kit/components';
import {ConfigService, Q9TransformPipe} from '@q9elements/ui-kit/common';
import {GetVersionNamePipe} from '../../../pipes/get-version-name.pipe';
import {GetVersionTagColorPipe} from '../../get-version-tag-color.pipe';
import {DiagramVersionItem} from '../../../../sidebar/shared/models/diagram-version-item.interface';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../../models/diagram-version.constant';
import {GetVersionLabelPipe} from '../../get-version-label.pipe';
import {DiagramJsonHandlerService} from '../../../../../diagram-data/diagram-json-handler.service';
import {AuthService} from 'src/app/core/auth/auth.service';

@Component({
  selector: 'app-version-list-item',
  templateUrl: './version-list-item.component.html',
  styleUrls: ['./version-list-item.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatButtonModule,
    TagComponent,
    GetVersionNamePipe,
    GetVersionTagColorPipe,
    TranslateModule,
    DatePipe,
    GetVersionLabelPipe,
    MatDividerModule,
    Q9TransformPipe
  ]
})
export class VersionListItemComponent {
  @Input() version: DiagramVersionItem;
  @Input() isAuthorizationActive = false;
  @Input() isAuthorisationInProgress = false;

  @Output() makeReady = new EventEmitter<void>();
  @Output() turnReadyAuthorization = new EventEmitter<boolean>();
  @Output() submitForAuthorization = new EventEmitter<void>();

  readonly DIAGRAM_VERSION_STATES_ENUM = DIAGRAM_VERSION_STATES_ENUM;
  public isMenuOpened = signal(false);
  public isHovered = signal(false);

  constructor(
    private diagramJsonSvc: DiagramJsonHandlerService,
    private authService: AuthService
  ) {}

  get userName(): string {
    const author = this.diagramJsonSvc.get().author;

    return `by ${author?.firstName} ${author?.lastName}`;
  }

  public makeReadyForRelease(): void {
    this.makeReady.emit();
  }

  public toggleReadyAuthorization(on: boolean): void {
    this.turnReadyAuthorization.emit(on);
  }

  public openSubmitForAuthorizationPopup(): void {
    this.submitForAuthorization.emit();
  }

  public openTargetRelease(): void {
    window.open(`${ConfigService.environment.RELEASE_URL}/${this.version.release?.id}`);
  }

  public handleMenuClick(event: MouseEvent) {
    this.isMenuOpened.set(true);
    event.stopPropagation();
  }

  public showMenuButton() {
    const hasPermissions = this.authService.canEditDiagram();
    const isDraft = this.version.state === DIAGRAM_VERSION_STATES_ENUM.DRAFT;
    const versionWithMenu = (hasPermissions && isDraft) || this.version.release;

    if (!versionWithMenu) {
      return of(false);
    }

    return this.diagramJsonSvc.diagramJson$.pipe(
      map(diagram => {
        const isCurrentState = diagram.state === this.version.state;

        return isCurrentState || this.isHovered() || this.isMenuOpened();
      })
    );
  }
}
